import React, { useLayoutEffect, useRef } from 'react';
import { FifaCard } from './FifaCard';

interface UserStats {
  totalGoals: number;
  totalAssists: number;
  totalSaves: number;
  totalMatches: number;
  totalWins: number;
  totalLosses: number;
  totalDraws: number;
  mvpCount: number;
  topScorerCount: number;
  topAssisterCount: number;
  topSaverCount: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  position?: string;
  rating: number;
  avatar?: string;
  description?: string;
}

interface ProfileHeaderProps {
  user: User | null;
  stats: UserStats | null;
  onEditProfile: () => void;
  onAvatarChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile?: boolean;
  showEditButtons?: boolean; // Nueva prop para controlar la visibilidad
  onDeleteAccount?: () => void; // Nueva prop para eliminar cuenta
  fixedDesktopHeight?: number;
  onHeightChange?: (height: number) => void;
}

export const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  stats,
  onEditProfile,
  onAvatarChange,
  isMobile = false,
  showEditButtons = true, // Por defecto mostrar botones
  onDeleteAccount,
  fixedDesktopHeight,
  onHeightChange
}) => {
  const hasFixedDesktopHeight = !isMobile && typeof fixedDesktopHeight === 'number';
  const rootRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (!onHeightChange) return;
    const target = rootRef.current;
    if (!target) return;

    const emitHeight = () => {
      const h = Math.ceil(target.offsetHeight);
      if (h > 0) onHeightChange(h);
    };

    emitHeight();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(emitHeight);
      observer.observe(target);
      return () => observer.disconnect();
    }

    window.addEventListener('resize', emitHeight);
    return () => window.removeEventListener('resize', emitHeight);
  }, [onHeightChange, user?.avatar, user?.description, stats, isMobile, showEditButtons, hasFixedDesktopHeight]);

  return (
    <div ref={rootRef} style={{
      backgroundColor: '#23272A',
      padding: isMobile ? '8px' : '28px', // Padding más compacto en mobile
      borderRadius: '18px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
      border: '1px solid #33383B',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: isMobile ? '10px' : '20px', // Gap más compacto en mobile
      flex: isMobile ? 'none' : '2',
      width: '100%',
      maxWidth: isMobile ? '100%' : 'none',
      boxSizing: 'border-box',
      minHeight: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : undefined,
      height: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : undefined,
      maxHeight: hasFixedDesktopHeight ? `${fixedDesktopHeight}px` : undefined,
      overflowY: hasFixedDesktopHeight ? 'auto' : 'visible',
    }}>
      {user && (
        <FifaCard 
          user={user} 
          stats={stats ? {
            totalGoals: stats.totalGoals,
            totalAssists: stats.totalAssists,
            totalSaves: stats.totalSaves,
            totalMatches: stats.totalMatches,
            totalWins: stats.totalWins,
            totalLosses: stats.totalLosses,
          } : null}
        />
      )}

      {/* Rating badge (estrellas + X/5) alineado a mobile */}
      {user?.rating != null && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '6px 12px',
            borderRadius: 10,
            background: '#2C2F33',
            border: '1px solid #40444B',
          }}
        >
          {[1, 2, 3, 4, 5].map((star) => (
            <span
              key={star}
              style={{
                color: star <= user.rating ? '#FFD700' : '#5C5E62',
                fontSize: 16,
              }}
            >
              {star <= user.rating ? '★' : '☆'}
            </span>
          ))}
          <span style={{ fontSize: 14, fontWeight: 600, color: '#FFFFFF', marginLeft: 4 }}>
            {user.rating}/5
          </span>
        </div>
      )}
      
      {/* Description Section */}
      {user?.description && (
        <div style={{
          width: '100%',
          padding: '16px',
          background: '#2C2F33',
          borderRadius: '12px',
          border: '1px solid #40444B',
          marginBottom: '16px'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#FFFFFF',
            margin: '0 0 8px 0',
            textAlign: 'center'
          }}>
            📝 Sobre mí
          </h3>
          <p style={{
            fontSize: '14px',
            color: '#B9BBBE',
            margin: 0,
            lineHeight: 1.5,
            textAlign: 'center',
            fontStyle: 'italic'
          }}>
            {user?.description}
          </p>
        </div>
      )}

      {/* Botones de perfil - Solo mostrar si showEditButtons es true */}
      {showEditButtons && (
        <div style={{
          display: 'flex',
          gap: '10px',
          flexDirection: isMobile ? 'column' : 'row',
          width: '100%'
        }}>
          <button
              onClick={onEditProfile}
              style={{
                padding: isMobile ? '10px 16px' : '12px 20px',
                backgroundColor: '#00E676',
                border: 'none',
                borderRadius: '8px',
                color: '#181A1B',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#00C853';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#00E676';
              }}
            >
              ✏️ Editar Perfil
            </button>
          
          <button
              onClick={() => document.getElementById('avatar-input')?.click()}
              style={{
                padding: isMobile ? '10px 16px' : '12px 20px',
                backgroundColor: '#7C4DFF',
                border: 'none',
                borderRadius: '8px',
                color: '#FFFFFF',
                cursor: 'pointer',
                fontSize: isMobile ? '14px' : '16px',
                fontWeight: '600',
                transition: 'background-color 0.2s ease',
                flex: 1
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#6A4C93';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#7C4DFF';
              }}
            >
              📷 Cambiar Foto
            </button>
        </div>
      )}

      {/* Botón de eliminar cuenta - Discreto y separado */}
      {showEditButtons && onDeleteAccount && (
        <div style={{
          width: '100%',
          marginTop: isMobile ? '8px' : '12px'
        }}>
          <button
            onClick={onDeleteAccount}
            style={{
              padding: isMobile ? '8px' : '10px',
              backgroundColor: 'transparent',
              border: 'none',
              color: '#EF4444',
              cursor: 'pointer',
              fontSize: isMobile ? '12px' : '13px',
              fontWeight: '500',
              transition: 'all 0.2s ease',
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              borderRadius: '6px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(239, 68, 68, 0.1)';
              e.currentTarget.style.color = '#DC2626';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#EF4444';
            }}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Eliminar Cuenta
          </button>
        </div>
      )}

      {/* Input oculto para seleccionar imagen */}
      {showEditButtons && (
        <input
          id="avatar-input"
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={onAvatarChange}
        />
      )}
    </div>
  );
}; 